<template>
  <SrpDetailsSummary class="last-minute-coordination" v-model:isOpened="isExpanded">
    <template #heading>
      <h4 class="global-h4">
        <span><span>Last-minute coordination?</span></span>
      </h4>
    </template>

    <template #details>
      <!-- 2+ days out -->
      <div class="last-minute-coordination__row">
        <h6 class="global-h6 last-minute-coordination__subtitle">2+ days before your visit</h6>
        <div>Message <a href="#" @click.prevent="emit('messageDestination')">the destination</a> with questions.</div>
        <div>If you haven't heard back, <a href="#" @click.prevent="emit('contactShrpa')">contact Shrpa</a>.</div>
      </div>
      <!-- / 2+ days out -->

      <!-- Day before or on-site -->
      <div class="last-minute-coordination__row">
        <h6 class="global-h6 last-minute-coordination__subtitle">Day before or on-site</h6>
        <template v-if="telHref">
          <div>Contact the destination directly:</div>
          <a class="last-minute-coordination__phone-link" :href="telHref">
            <IconEmbedded name="phone_1-5" :size="20" color="#1f3320" />
            <span>
              <template v-if="props.contactName">{{ props.contactName }} &middot; </template>
              <MaskedPhoneNumber :phoneNumber="props.phoneNumber" />
            </span>
          </a>
        </template>
        <!-- Older visits were created before the destination's phone number was required -->
        <div v-else>No phone number yet. <a href="#" @click.prevent="emit('messageDestination')">Message them</a> or <a href="#" @click.prevent="emit('contactShrpa')">contact Shrpa</a>.</div>
      </div>
      <!-- / Day before or on-site -->

      <!-- Anytime -->
      <div class="last-minute-coordination__row">
        <!-- <h6 class="global-h6 last-minute-coordination__subtitle">Anytime</h6> -->
        <div>
          <a href="#" @click.prevent="emit('contactShrpa')">Contact Shrpa</a> anytime with questions.<br />For lodging or comps, try the destination first (<a
            href="#"
            @click.prevent="emit('messageDestination')"
            >message them</a
          >, or text if last-minute).
        </div>
      </div>
      <!-- / Anytime -->
    </template>
  </SrpDetailsSummary>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { phoneToTelHref } from "@logic/PhoneMask";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import MaskedPhoneNumber from "@components/MaskedPhoneNumber.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";

// Who the creator should contact (and how) as their visit gets close.
// Meant to sit inside a green NoteWithIcon (on the Plan tab of the creator's visit page), which is what styles the links.
const props = defineProps<{
  collabInputId: string;
  contactName: string | null;
  phoneNumber: string | null;
}>();

const emit = defineEmits<{
  (e: "messageDestination"): void;
  (e: "contactShrpa"): void;
}>();

const telHref = computed<string | null>(() => phoneToTelHref(props.phoneNumber));

// Expand/collapse ============================================================
// Open by default. Collapsing is remembered per visit (it's at the top of the tab the page opens on, so it shouldn't keep popping back open once they've read it).
const collapsedStorageKey = `lastMinuteCoordinationCollapsed-${props.collabInputId}`;
const isExpanded = ref<boolean>(localStorage.getItem(collapsedStorageKey) !== "true");

watch(isExpanded, () => {
  if (isExpanded.value) localStorage.removeItem(collapsedStorageKey);
  else localStorage.setItem(collapsedStorageKey, "true");
});
</script>

<style scoped lang="scss">
// Last-minute coordination ===================================================
.last-minute-coordination {
  // SrpDetailsSummary's default gap under the heading is looser than this note wants
  :deep(.srp-summary__details--with-top-margin) {
    margin-top: 10px;
  }

  &__row {
    & + & {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px rgba(0, 0, 0, 0.15) solid;
    }
  }

  &__subtitle {
    margin-bottom: 2px;
  }

  &__phone-link {
    margin-top: 4px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    // font-weight: bold;
  }
}
</style>
